/**
 * Function math utilities — pure, DOM-free, no Math.random().
 *
 * Functions are represented as ReadonlyMap<string, string> (FnMap).
 * The domain is passed explicitly so partial maps can be detected.
 */

// ---------------------------------------------------------------------------
// Canonical sort (mirrors finite-set.ts)
// ---------------------------------------------------------------------------

function canonicalSort(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (!isNaN(na) && !isNaN(nb)) {
    return na - nb;
  }
  return a < b ? -1 : a > b ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** A mathematical function as an explicit mapping from input strings to output strings. */
export type FnMap = ReadonlyMap<string, string>;

// ---------------------------------------------------------------------------
// Predicates
// ---------------------------------------------------------------------------

/**
 * Returns true iff every element of `domain` has exactly one image in `map`
 * (i.e. the map is defined on the full domain).
 */
export function isFunction(map: FnMap, domain: readonly string[]): boolean {
  return domain.every((x) => map.has(x));
}

/**
 * Returns true iff no two distinct keys in `map` share the same output value.
 * (Injectivity / one-to-one.)
 */
export function isInjective(map: FnMap): boolean {
  const seen = new Set<string>();
  for (const v of map.values()) {
    if (seen.has(v)) {
      return false;
    }
    seen.add(v);
  }
  return true;
}

/**
 * Returns true iff every element of `codomain` appears as a value in `map`.
 * (Surjectivity / onto.)
 */
export function isSurjective(map: FnMap, codomain: readonly string[]): boolean {
  const values = new Set(map.values());
  return codomain.every((y) => values.has(y));
}

/**
 * Returns true iff the map is a function on `domain`, injective, and surjective onto `codomain`.
 */
export function isBijective(
  map: FnMap,
  domain: readonly string[],
  codomain: readonly string[],
): boolean {
  return isFunction(map, domain) && isInjective(map) && isSurjective(map, codomain);
}

// ---------------------------------------------------------------------------
// Image
// ---------------------------------------------------------------------------

/**
 * Returns the image of `domain` under `map`: { map(x) : x ∈ domain, x has a mapping }.
 * The result is deduped and sorted in canonical (numeric-aware) order.
 */
export function image(map: FnMap, domain: readonly string[]): string[] {
  const outputs: string[] = [];
  for (const x of domain) {
    const v = map.get(x);
    if (v !== undefined) {
      outputs.push(v);
    }
  }
  return [...new Set(outputs)].sort(canonicalSort);
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * Computes the composition (g ∘ f): for each key x of f whose value f(x) is
 * a key of g, the result maps x → g(f(x)).
 * Entries where f(x) is not in the domain of g are omitted.
 */
export function compose(g: FnMap, f: FnMap): FnMap {
  const result = new Map<string, string>();
  for (const [x, fx] of f) {
    const gfx = g.get(fx);
    if (gfx !== undefined) {
      result.set(x, gfx);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Inverse
// ---------------------------------------------------------------------------

/**
 * Returns the inverse map (value → key) if `map` is injective, or null otherwise.
 * When the map is bijective onto its image the result is the true inverse function.
 */
export function inverse(map: FnMap): FnMap | null {
  if (!isInjective(map)) {
    return null;
  }
  const result = new Map<string, string>();
  for (const [k, v] of map) {
    result.set(v, k);
  }
  return result;
}
